import type { ExchangeRateDocType } from '../db/schemas';

export type WeekendRatePolicy = 'USE_FRIDAY_EMISSION' | 'USE_FRIDAY_VALID';

/**
 * Resolves the applicable exchange rate for a given transaction date,
 * evaluating the clean [since, until] range.
 */
export function getRateForDate(
  transactionDate: string,
  rates: ExchangeRateDocType[],
  _policy: WeekendRatePolicy = 'USE_FRIDAY_EMISSION'
): ExchangeRateDocType | null {
  if (!rates || rates.length === 0) return null;

  const targetDateStr = transactionDate.substring(0, 10);

  // 1. Direct Range Match: since <= targetDateStr <= until
  for (const rate of rates) {
    if (!rate.since || !rate.until || rate.is_deleted) continue;
    if (rate.since <= targetDateStr && targetDateStr <= rate.until) {
      return rate;
    }
  }

  // 2. Fallback: Return the closest rate before the transaction date
  const sortedRates = [...rates]
    .filter((r) => !r.is_deleted && r.since)
    .sort((a, b) => b.since.localeCompare(a.since));

  for (const rate of sortedRates) {
    if (rate.since <= targetDateStr) {
      return rate;
    }
  }

  // 3. Final Fallback: Return oldest available rate
  return sortedRates[sortedRates.length - 1] || null;
}



export function getLatestRate(
  rates: ExchangeRateDocType[],
  currency: string,
): { rate: number; emision: string } | null {
  const currencyKey = currency.toLowerCase();
  const filtered = rates
    .filter((r) => !r.is_deleted)
    .sort((a, b) => (b.until || b.since || '').localeCompare(a.until || a.since || ''));

  if (filtered.length === 0) return null;

  const latestDoc = filtered[0];
  const rateVal = (latestDoc as any)[currencyKey];

  if (typeof rateVal === 'number' && rateVal > 0) {
    return {
      rate: rateVal,
      emision: latestDoc.since || '',
    };
  }

  return null;
}

export type { ExchangeRateDocType };
